/**
 * DayCell — 5 visuelle Zustände (Sprint-16-Brand nach Mockup 4d).
 *
 * Zustände:
 *   1. Past          → opacity-40, nicht klickbar
 *   2. Not-in-month  → opacity-30, nicht klickbar (Grid-Padding)
 *   3. Default       → cream cell, Preis in ink-faint (Fallback-Rate, kein ARI-Push)
 *   4. Custom-Rate   → cream cell + Preis in ink bold + AMBER-DOT rechts oben
 *                      (Signature-Repeat: amber = live-signal, matches Wordmark-○)
 *   5. Belegt        → sage-tint bg + Preis in sage-dark
 *   6. Stop-Sell     → sealwax-tint bg + Preis durchgestrichen + Ban-Icon
 *   7. Verbund       → postmark-tint bg + Link2-Icon: Gesamtwohnung oder Zimmer belegt die Nacht
 *   8. Doppelt       → Strich am unteren Rand: gebucht, und der Verbund belegt dieselbe Nacht
 *
 * Today: sealwax-ring (Signature-Marker, matches Kalender-H2-Today-Column-Tint)
 */
import { Ban, Link2 } from 'lucide-react';
import {
  DOUBLE_BOOKED_LABEL,
  DoubleBookedMark,
} from '@/features/properties/flat-family/DoubleBookedMark';
import { cn } from '@/shared/lib/cn';
import { FAMILY_CLOSED_LABEL, formatShortRate, type ResolvedDay } from './calendarHelpers';

interface DayCellProps {
  day: ResolvedDay;
  currency: string;
  onClick?: () => void;
}

export function DayCell({ day, currency, onClick }: DayCellProps) {
  const hasRate = day.rate != null && day.rate > 0;
  const rateLabel = hasRate ? formatShortRate(day.rate!, currency) : '—';
  const className = cn(
    'group relative flex h-20 flex-col items-stretch justify-between rounded-md border p-2 text-left transition-colors',
    !day.stopSell && !day.isBooked && 'bg-card border-line/50',
    !day.stopSell && day.closedByFamily && 'bg-postmark/6 border-postmark/15',
    !day.stopSell && !day.isBooked && onClick && 'hover:border-ink-faint/40',
    day.isBooked && 'bg-sage/10 border-sage/30',
    day.isBooked && onClick && 'hover:border-sage/50',
    day.stopSell && 'bg-sealwax/10 border-sealwax/30',
    !day.inMonth && 'opacity-30',
    (day.isPast || !onClick) && 'cursor-default opacity-70',
    day.isToday && 'ring-sealwax/40 ring-2 ring-inset',
  );
  const contents = (
    <>
      <span className="text-ink-soft text-[11px] font-medium tabular-nums leading-none">
        {new Date(day.date).getUTCDate()}
      </span>
      <div className="flex items-end justify-end gap-1">
        {day.stopSell && (
          <Ban size={12} strokeWidth={2} className="text-sealwax mr-auto" aria-label="Stop-Sell" />
        )}
        {!day.stopSell && day.closedByFamily && (
          <Link2
            size={12}
            strokeWidth={2}
            className="text-postmark/60 mr-auto"
            aria-label={FAMILY_CLOSED_LABEL}
          />
        )}
        <span
          className={cn(
            'text-right font-serif text-base tabular-nums leading-none',
            day.stopSell
              ? 'text-sealwax line-through'
              : day.isBooked
                ? 'text-sage font-medium'
                : day.fromChannex
                  ? 'text-ink font-medium'
                  : 'text-ink-faint',
          )}
        >
          {rateLabel}
        </span>
      </div>
      {day.fromChannex && !day.stopSell && (
        <span
          className="bg-amber absolute right-1.5 top-1.5 size-1.5 rounded-full"
          aria-label="Live aus Channex"
        />
      )}
      {day.doubleBooked && (
        <span
          role="img"
          aria-label={DOUBLE_BOOKED_LABEL}
          title={DOUBLE_BOOKED_LABEL}
          className="absolute inset-x-2 bottom-1"
        >
          <DoubleBookedMark />
        </span>
      )}
    </>
  );

  if (!onClick) {
    return (
      <div className={className} aria-label={`${day.date}: nur lesen`}>
        {contents}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={day.isPast || !day.inMonth}
      className={className}
    >
      {contents}
    </button>
  );
}
